import { useMatches, useParams, useNavigate } from 'react-router-dom'
import { useAuth, useGuard, useHasAccess, useUser } from '../../hooks/auth'
import { LOCAL_EXECUTION } from '../../utils/featureFlag'
import {
  DashboardhMediumButton,
  DashboardMessage,
  DashboardFinancialCloseHeader,
  DashboardFinancialDropdown,
  Table,
  DashboardTransactionItem,
  DashboardFinancialLink,
  Modal,
  DashboardAlertMessage,
} from 'bd-web-components'
import {
  Container,
  FormDivider,
  TextHeaderBox,
  PageTitle,
  PageHeaderBox,
  PageSubtitle,
  ButtonHeaderBox,
  TableContainer,
  ExceptionContainer,
  LoaderBox,
  TableTag,
  Button,
  FinancialClosingButtons,
} from './styles'
import { useCallback, useEffect, useState } from 'react'
import { getDashColor } from '../../styles'
import {
  PRIMARY_RED_03,
  SECONDARY_GREEN_07,
} from 'bd-web-components/src/utils/dash-colors'
import Loader from '../../components/Loader'
import {
  useApproveReject,
  useFinancialClosing,
} from '../../hooks/financial-close'
import { financialCloseCreditAndDebit } from '../../utils/table-modeling'
import { useFinancialClosingTransactions } from '../../hooks/financial-close-transactions'
import { TransactionItemModal } from '../../components/FinancialTransactionItem'
import currencyFormatter from 'currency-formatter'
import { useFinancialClosingDebits } from '../../hooks/financial-close-debits'
import { useFinancialClosingAdjustments } from '../../hooks/financial-close-adjustments'
import { DropdownContainer } from '../../components/DropdownContainer'
import { useEvent } from '../../hooks/events'
import { useVerifyNps } from '../../hooks/verify-nps'
import { useBoolean } from '../../hooks/use-boolean'
import { EventDataNPS, NPSModal } from '../../components/NPSModal'
import { Breadcrumb } from '../../components/Breadcrumb'

export function FinancialClosing() {
  const authContext = useAuth()
  const { id } = useParams<{ id: string }>()
  const eventId = Number(id)
  const { authHookWithEvent } = authContext
  const navigate = useNavigate()

  if (!LOCAL_EXECUTION) {
    authHookWithEvent(id!, navigate)
  }
  // verify if user has permission to acess
  useGuard(['DP7'])

  const { event } = useEvent(Number(id))

  const accessRequestWithdraw =
    useHasAccess('DP3') &&
    (event?.clienteId === authContext?.user?.clienteId ||
      authContext?.user?.clienteId == null)
  const screenLimit = 762

  let matches = useMatches()
  let crumbs = matches
    .filter((match: any) => Boolean(match.handle?.crumb))
    .map((match: any) => {
      return {
        name: match.handle.crumb(match.data),
        path: match.pathname,
      }
    })

  const [screenWidth, setScreenWidth] = useState(window.innerWidth)
  const [isOpen, setIsOpen] = useState(false)
  const [modalData, setModalData] = useState<any | null>(null)
  const [isMobile, setIsMobile] = useState<boolean>(screenWidth < screenLimit)
  const [hasNPS, setHasNPS] = useState<boolean>(false)
  const [eventoData, setEventoData] = useState<EventDataNPS>({
    event_name: '',
    pracaId: '',
    praca: '',
  })
  const columns = financialCloseCreditAndDebit(isMobile)
  const NPSService = 'Fechamento'
  const NPSLoading = useBoolean()
  const user = useUser()
  const { NPS } = useVerifyNps(user.id.toString())

  const handlerModal = (data: any) => {
    setModalData(data)
    setIsOpen(true)
  }

  const handleWindowSizeChange = useCallback(() => {
    setScreenWidth(window.innerWidth)
  }, [])

  const handlerNavigate = (url: string) => {
    navigate(url)
  }

  // REQUESTS
  const { debits } = useFinancialClosingDebits(eventId)
  const { transactions } = useFinancialClosingTransactions(eventId)
  const { adjustments } = useFinancialClosingAdjustments(eventId)
  const { credit, mutate } = useFinancialClosing(eventId)
  const eventData = useEvent(eventId)

  const hasManagerFinancialClosePermission = useHasAccess('E23')
  const hasApproveRejectFinancialClosePermission = useHasAccess('E22')
  const canCloseFinancial =
    (credit.financialState === 'Submetido' &&
      hasManagerFinancialClosePermission) ||
    ((hasManagerFinancialClosePermission ||
      hasApproveRejectFinancialClosePermission) &&
      credit.financialState === 'Aprovado Comercial' &&
      event?.clienteId === authContext?.user?.clienteId)

  useEffect(() => {
    if (credit.eventName !== '') {
      document.title = credit.eventName
    }
  }, [debits, transactions, adjustments, credit])

  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange)
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange)
    }
  }, [handleWindowSizeChange])

  useEffect(() => {
    setIsMobile(screenWidth < screenLimit)
  }, [screenWidth])

  useEffect(() => {
    if (NPS !== undefined) {
      console.log(NPS, 'NPS')
      const data = NPS.data.data
      setHasNPS(data)
    }
  }, [NPS])

  useEffect(() => {
    if (eventData.event !== undefined) {
      setEventoData({
        event_name: eventData.event.nome,
        pracaId: eventData.event.pracaId!,
        praca: eventData.event.praca!,
      })
    }
  }, [
    eventData.event,
    eventData.events,
    eventData.isLoading,
    eventData.isError,
  ])

  const total = currencyFormatter.format(
    credit.creditRawTotal +
    debits.debitsRawTotal +
    adjustments.adjustmentsRawTotal +
    transactions.transactionsRawTotal,
    { code: 'BRL' }
  )

  const [modalOpenType, setModalOpenType] =
    useState<'approve' | 'reject' | null>(null)
  const {
    isMutating: isLoadingApproveReject,
    error: errorApproveReject,
    trigger: triggerApproveReject,
  } = useApproveReject(eventId)

  const handleApproveReject = useCallback(
    async (action: 'approve' | 'reject') => {
      if (isLoadingApproveReject) return
      await triggerApproveReject(action)
      mutate()
      setModalOpenType(null)
      if (hasNPS) {
        setTimeout(() => {
          NPSLoading.onTrue()
        }, 500)
      }
    },
    [isLoadingApproveReject, NPSLoading, hasNPS, triggerApproveReject, mutate]
  )

  return (
    <>
      <Container>
        <Breadcrumb
          paths={[
            { name: 'Início', path: '/events' },
            { name: 'Evento', path: `/events/${id}` },
            ...crumbs,
          ]}
        />

        {!isMobile && (
          <>
            <PageHeaderBox>
              <TextHeaderBox>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    width: '100%',
                    marginTop: '40px',
                    marginBottom: '10px',
                    height: 'max-content',
                  }}
                >
                  <PageTitle>Fechamento Financeiro</PageTitle>
                  <PageTitle>{credit.eventName}</PageTitle>
                </div>
                <PageSubtitle>
                  Veja todas as informações do seu fechamento financeiro de
                  forma transparente.
                </PageSubtitle>
              </TextHeaderBox>
              <ButtonHeaderBox>
                {accessRequestWithdraw && (
                  <DashboardhMediumButton
                    onClick={() =>
                      handlerNavigate(`/events/${id}/withdraw/requests/list`)
                    }
                    color="#fff"
                    background={getDashColor('SECONDARY_BLUE_01', 'GRAY_DARK')}
                    height="48px"
                    maxWidth="100%"
                    variant="contained"
                    width="204px"
                    borderRadius='100px'
                  >
                    {'Solicitar Saque'}
                  </DashboardhMediumButton>
                )}
              </ButtonHeaderBox>
            </PageHeaderBox>
            <FormDivider />
          </>
        )}
        <div>
          {/* ERROR CASE */}
          {credit.creditData === null && credit.isLoading === false && (
            <ExceptionContainer height={400} margin={false}>
              <DashboardMessage
                isError={true}
                isImage={true}
                maintext={'Um erro aconteceu...'}
                subtext={'Já estamos trabalhando para corrigir o problema'}
              />
            </ExceptionContainer>
          )}
          {/* SUCCESS CASE */}
          {!credit.isLoading && (
            <TableContainer>
              {isMobile && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    width: '100%',
                    height: 'max-content',
                  }}
                >
                  <PageTitle>Fechamento Financeiro</PageTitle>
                  <PageTitle>{credit.eventName}</PageTitle>
                </div>
              )}
              <div>
                <DashboardFinancialCloseHeader
                  headertitle={'Saldo Total'}
                  toolTip={true}
                  tip="O saldo total é diferente do saldo disponível. Esses valores variam de acordo com o status do fechamento do evento, e a proposta acordada. Para saber mais, entre em contato com o seu consultor responsável."
                  value={total}
                  mobileButtonText={'Solicitar Saque'}
                  showButton={accessRequestWithdraw}
                  buttonUrl={`/events/${id}/withdraw/requests/list`}
                  status={credit.financialState}
                />
              </div>
              {canCloseFinancial && (
                <DashboardFinancialDropdown
                  title="Fechamento Financeiro  💼"
                  subtitle={
                    'Para sua segurança, o fechamento precisa ser revisado e aprovado'
                  }
                  value={
                    <FinancialClosingButtons>
                      <Button
                        className="outlined"
                        onClick={() => setModalOpenType('reject')}
                      >
                        Rejeitar
                      </Button>
                      <Button onClick={() => setModalOpenType('approve')}>
                        Aprovar
                      </Button>
                    </FinancialClosingButtons>
                  }
                  open={false}
                  variant="danger"
                  disableDropdown
                />
              )}
              {/* RECEITAS DROPDOWN */}
              <DropdownContainer
                valueTotalFormated={
                  credit.creditFormatedTotal !== null
                    ? credit.creditFormatedTotal
                    : 'R$ -'
                }
                open={true}
                mainData={credit.creditData}
                loading={credit.isLoading}
                columns={columns}
                color={SECONDARY_GREEN_07}
                dropdownTitle={'Receitas do Evento 💰'}
                dropdownSubtitle={'Tudo que entrou de saldo no evento'}
                linkText={'Saiba o que significa cada categoria'}
                linkHref={'https://bilheteriadigital.zendesk.com/hc/pt-br'}
                isBlank={true}
              />

              {/* DESPESAS DROPDOWN */}
              <DashboardFinancialDropdown
                title={'Despesas do Evento 💸'}
                subtitle={'Tudo o que houve de despesas no evento'}
                value={
                  debits.debitsFormatedTotal !== null
                    ? debits.debitsFormatedTotal
                    : 'R$ -'
                }
                open={true}
                color={PRIMARY_RED_03}
              >
                {true && (
                  <>
                    {debits.servicesData[0] !== undefined && (
                      <>
                        <TableTag>Serviços</TableTag>
                        <Table
                          columns={columns}
                          data={debits.servicesData}
                          pageCount={0}
                        />
                      </>
                    )}
                    {debits.otherDebitsData[0] !== undefined && (
                      <>
                        <TableTag>Outros Débitos</TableTag>
                        <Table
                          columns={columns}
                          data={debits.otherDebitsData}
                          pageCount={0}
                        />
                      </>
                    )}
                    <DashboardFinancialLink
                      text={'Saiba o que significa cada categoria'}
                      href={'https://bilheteriadigital.zendesk.com/hc/pt-br'}
                      isBlank={true}
                    />
                  </>
                )}
                {debits.isLoading && (
                  <LoaderBox>
                    <Loader noMargin={true} />
                  </LoaderBox>
                )}
              </DashboardFinancialDropdown>

              {/* AJUSTES DROPDOWN */}
              <DropdownContainer
                valueTotalFormated={
                  adjustments.adjustmentsFormatedTotal !== null
                    ? adjustments.adjustmentsFormatedTotal
                    : 'R$ -'
                }
                open={true}
                mainData={adjustments.adjustmentsData}
                loading={adjustments.isLoading}
                columns={columns}
                color={
                  adjustments.adjustmentsRawTotal > 0
                    ? SECONDARY_GREEN_07
                    : PRIMARY_RED_03
                }
                dropdownTitle={'Ajustes ⚙️'}
                dropdownSubtitle={
                  'Todos os ajustes que foram feitos no fechamento'
                }
                linkText={'Saiba o que significa cada categoria'}
                linkHref={'https://bilheteriadigital.zendesk.com/hc/pt-br'}
                isBlank={true}
              />

              {/* TRANSACOES DROPDOWN */}
              <DashboardFinancialDropdown
                title={'Transações 🔄'}
                subtitle={'Todas as transações que aconteceram nesse evento'}
                tipText={'(apenas os repasses são contabilizados)'}
                value={
                  transactions.transactionsFormatedTotal !== null
                    ? transactions.transactionsFormatedTotal
                    : '-'
                }
                open={true}
              >
                <br />
                <TableTag>Repasses</TableTag>
                {transactions.transactionsData.withdraw && (
                  <>
                    {transactions.transactionsData.withdraw.map(
                      (transaction: any) => {
                        return (
                          <div
                            style={{
                              cursor: 'pointer',
                            }}
                            onClick={() => handlerModal(transaction)}
                          >
                            <DashboardTransactionItem
                              onClick={() => handlerModal(transaction)}
                              title={transaction.title}
                              subtitle={transaction.description}
                              value={transaction.value}
                              type={transaction.type}
                              open={true}
                            />
                          </div>
                        )
                      }
                    )}
                  </>
                )}
                <br />
                <TableTag>Antecipações</TableTag>
                {transactions.transactionsData.advance && (
                  <>
                    {transactions.transactionsData.advance.map(
                      (transaction: any) => {
                        return (
                          <div
                            style={{
                              cursor: 'pointer',
                            }}
                            onClick={() => handlerModal(transaction)}
                          >
                            <DashboardTransactionItem
                              onClick={() => handlerModal(transaction)}
                              title={transaction.title}
                              subtitle={transaction.description}
                              value={transaction.value}
                              type={transaction.type}
                              open={false}
                            />
                          </div>
                        )
                      }
                    )}
                  </>
                )}
                <DashboardFinancialLink
                  text={'Saiba o que significa cada categoria'}
                  href={'https://bilheteriadigital.zendesk.com/hc/pt-br'}
                  isBlank={true}
                />
                {transactions.isLoading && (
                  <LoaderBox>
                    <Loader noMargin={true} />
                  </LoaderBox>
                )}
              </DashboardFinancialDropdown>

              {/* GMV DROPDOWN */}
              <DashboardFinancialDropdown
                title={'Venda Total do Evento 💵'}
                subtitle={
                  'Valor total das vendas do evento, realizadas pela ticketeira e em dinheiro.'
                }
                value={credit.financialGMV !== null ? credit.financialGMV : '-'}
                tipText={'(pontos físicos e comissários)'}
                open={false}
              >
                <></>
              </DashboardFinancialDropdown>
            </TableContainer>
          )}
          {/* LOADING CASE */}
          {credit.isLoading && (
            <ExceptionContainer height={400} margin={false}>
              <Loader />
            </ExceptionContainer>
          )}
        </div>
        <TransactionItemModal
          eventName={credit !== undefined ? credit.eventName : ''}
          isOpen={isOpen}
          handleClose={() => setIsOpen(!isOpen)}
          data={modalData}
          eventId={id as string}
        />
        <Modal
          hasHeader
          isShowing={modalOpenType === 'approve'}
          title="Fechamento Financeiro"
          subTitle={
            <>
              Você deseja <b>confirmar</b> o fechamento do evento?
            </>
          }
          onRequestClose={() => setModalOpenType(null)}
        >
          <FinancialClosingButtons className="block">
            {isLoadingApproveReject ? (
              <LoaderBox>
                <Loader noMargin={true} />
              </LoaderBox>
            ) : (
              <>
                {errorApproveReject && !isLoadingApproveReject && (
                  <DashboardAlertMessage
                    mainMessage={'Oops!'}
                    subMessage={errorApproveReject}
                    iconName="report"
                  />
                )}
                <Button
                  className="success large"
                  onClick={() => handleApproveReject('approve')}
                >
                  Confirmar
                </Button>
                <Button
                  className="outlined default large"
                  onClick={() => setModalOpenType(null)}
                >
                  Cancelar
                </Button>
              </>
            )}
          </FinancialClosingButtons>
        </Modal>
        <Modal
          hasHeader
          isShowing={modalOpenType === 'reject'}
          title="Fechamento Financeiro"
          subTitle={
            <>
              Você deseja <b>rejeitar</b> o fechamento do evento?
            </>
          }
          onRequestClose={() => setModalOpenType(null)}
        >
          <FinancialClosingButtons className="block">
            {isLoadingApproveReject ? (
              <LoaderBox>
                <Loader noMargin={true} />
              </LoaderBox>
            ) : (
              <>
                {errorApproveReject && !isLoadingApproveReject && (
                  <DashboardAlertMessage
                    mainMessage={'Oops!'}
                    subMessage={errorApproveReject}
                    iconName="report"
                  />
                )}
                <Button
                  className="large"
                  onClick={() => handleApproveReject('reject')}
                >
                  Rejeitar
                </Button>
                <Button
                  className="outlined default large"
                  onClick={() => setModalOpenType(null)}
                >
                  Cancelar
                </Button>
              </>
            )}
          </FinancialClosingButtons>
        </Modal>
        <NPSModal
          user={user as any}
          event={{
            id: id!,
            event_name: eventoData.event_name,
            pracaId: eventoData.pracaId,
            praca: eventoData.praca,
          }}
          service={NPSService}
          isOpen={NPSLoading.value}
          onRequestClose={() => {
            NPSLoading.onFalse()
          }}
        />
      </Container>
    </>
  )
}
